import { alpha, type Theme } from '@mui/material/styles'

/**
 * The house scrollbar, applied globally by `VeraThemeProvider` via `<GlobalStyles>`.
 *
 * The browser default is an opaque light-grey trough with a chunky thumb. It ignores the
 * theme entirely (so it glares against a near-black page in dark mode) and it is drawn
 * hard against the edge of its container. This replaces it everywhere at once with a
 * thin, theme-aware, near-invisible bar that darkens on hover.
 *
 * Global rather than per-component on purpose: every scroll container in the platform
 * inherits it, so a new page cannot ship the browser default by omission.
 *
 * The `border: transparent` + `background-clip: content-box` pair is what insets the
 * thumb off the container edge -- the border is part of the scrollbar box but paints
 * nothing, so the visible thumb is (width - 2*border) wide and floats clear of the
 * content. That is the "subtle bar beside the grid" look rather than one jammed against
 * it.
 *
 * The `*` selector reaches every scroller but stays the *weakest* rule in the cascade,
 * so any component that opts out via the `hideScrollbar` mixin (SideNav, SectionNav)
 * still wins on class specificity and keeps its scrollbar hidden.
 *
 * Delivered as a GlobalStyles object rather than appended to the `MuiCssBaseline`
 * style string: the baseline's string is run through stylis, which dropped these rules
 * silently (the `body` rule in the same string survived, these did not). An object
 * passed to GlobalStyles is inserted verbatim and is trivially verifiable in devtools.
 */
export const scrollbar = (t: Theme) => {
  // Neutral rather than brand-tinted: a scrollbar is chrome, not an accent, and a purple
  // bar next to every table would compete with the real brand affordances.
  const thumb = alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.16 : 0.18)
  const thumbHover = alpha(t.palette.text.primary, t.palette.mode === 'dark' ? 0.32 : 0.34)

  return {
    '*': {
      scrollbarWidth: 'thin' as const,
      scrollbarColor: `${thumb} transparent`,
    },
    '*::-webkit-scrollbar': {
      width: 10,
      height: 10,
    },
    '*::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '*::-webkit-scrollbar-thumb': {
      backgroundColor: thumb,
      backgroundClip: 'content-box',
      border: '3px solid transparent',
      borderRadius: 8,
    },
    '*::-webkit-scrollbar-thumb:hover': {
      backgroundColor: thumbHover,
    },
    '*::-webkit-scrollbar-corner': {
      background: 'transparent',
    },
  }
}
